import { Box, CollapsibleCard, IconButton } from '@atgfr8/atg-ui-tailwind';
import { AutoComplete } from '@components';
import { customerDetailsStore } from '@store/customers/customerDetails';
import {
  customerLTLSettings,
  LTLBlockedCarrier,
} from '@store/customers/customerDetails/LTL';
import { userStore } from '@store/user';
import { DeleteIcon, CheckIcon, AccountIcon } from '@atgfr8/icons';
import { CarrierInfo } from '@typeDefinitions/carrierTypes';
import { size } from 'lodash';
import { Accessor, For, Setter, Show } from 'solid-js';

import { InitialLTLFormValues } from '../Ltl';
import { blockedCarriers as classes } from './classes';

const BlockedCarriers = (props: {
  setFields: (key: string, value: unknown) => void;
  data: Accessor<InitialLTLFormValues>;
  isValidCarrierChoice: (carrier: CarrierInfo) => boolean;
  setIsDirty: Setter<boolean>;
  onSectionChange: () => void;
}) => {
  const onDelete = (item: LTLBlockedCarrier) => {
    const blockedCarrierList = props
      .data()
      .ltlBlockedCarriers.filter((i) => i.carrierId !== item.carrierId);
    props.setIsDirty(true);
    props.onSectionChange();
    props.setFields('ltlBlockedCarriers', blockedCarrierList);
  };

  const onItemSelect = (item: CarrierInfo) => {
    if (!props.isValidCarrierChoice(item)) {
      const lineItems = [...props.data().ltlBlockedCarriers];
      lineItems.push({
        carrierName: item.name,
        carrierMC: item.mC,
        carrierDOT: item.dOT,
        carrierId: item.id,
        userId: userStore.user.id,
        customerId: customerDetailsStore.customer.id as number,
        status: item.status,
      });
      props.setIsDirty(true);
      props.onSectionChange();
      props.setFields('ltlBlockedCarriers', lineItems);
    }
  };

  return (
    <CollapsibleCard
      startTitle="Blocked Carriers"
      loading={customerLTLSettings.isLoading}
      class="mb-2"
    >
      <Box class={classes.blockedContainer}>
        Prevent customers from seeing quotes from carriers you add here.
      </Box>

      <AutoComplete
        id="autocomplete-lane-search-delivering-to"
        endpoint="ltl/autocomplete/?term="
        onItemSelect={(item) => onItemSelect(item as CarrierInfo)}
        multipleValueRender={() => {
          return (
            <For each={props.data().ltlBlockedCarriers}>
              {(item) => (
                <Box class="flex mt-1 justify-between p-2 border-2 shadow-xs">
                  <Box class="flex gap-8 items-center">
                    <AccountIcon class="text-[#026EA1]" />
                    <Box class="font-medium text-base">
                      {item.carrierName}
                      <Box class={classes.carrierMcDotText}>
                        MC:{' '}
                        <span class="text-sm! font-normal">
                          {item.carrierMC}
                        </span>{' '}
                        | DOT:{' '}
                        <span class="text-sm! font-normal">
                          {item.carrierDOT}
                        </span>
                      </Box>
                    </Box>
                  </Box>
                  <Box class="flex flex-col items-center justify-center w-14">
                    <IconButton
                      onClick={() => onDelete(item)}
                      dataTestId="blocked-carriers-delete-icon-button"
                    >
                      <DeleteIcon class="text-[22px]! text-red-700" />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </For>
          );
        }}
        renderMultipleKey="carrierName"
        mode="multiple"
        defaultValue=""
        label=""
        placeholder="Add one or more Carriers ..."
        version="v3"
        renderItem={(item) => {
          const item2 = item as CarrierInfo;
          return (
            <Box class="flex gap-1">
              <AccountIcon class="text-[#026EA1]" />
              <Box class="font-medium text-base">
                {item2.name}

                <Box class={classes.carrierMcDotText}>
                  MC: <span class="text-sm! font-normal">{item2.mC}</span> |
                  DOT: <span class="text-sm! font-normal">{item2.dOT}</span>
                </Box>
                <Box class={classes.carrierMcDotText}>
                  Status:{' '}
                  <span
                    class={`font-bold ${
                      item2.status === 'Active'
                        ? 'text-[#28957C]'
                        : 'text-[#B00020]'
                    }`}
                  >
                    {item2.status}
                  </span>
                </Box>
              </Box>

              {item2.lTLEnabled && (
                <Box class="flex flex-col w-14">
                  <span class={classes.carrierLTLBox}>
                    LTL
                    <CheckIcon class="text-[13px]! text-white" />
                  </span>
                </Box>
              )}
            </Box>
          );
        }}
      />
      <Show when={size(props.data().ltlBlockedCarriers) === 0}>
        <Box class={classes.noCarrierCotainer}>
          <Box class={classes.noCarrierText}>No carriers have been blocked</Box>
        </Box>
      </Show>
    </CollapsibleCard>
  );
};

export default BlockedCarriers;
